const PRODUCTION_HOSTS = new Set([
  "mozzoapp.com",
  "www.mozzoapp.com",
  "backoffice.mozzoapp.com",
]);

const BETA_HOSTS = new Set([
  "beta.mozzoapp.com",
  "backoffice.beta.mozzoapp.com",
]);

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "::1", "[::1]"]);

export const PRODUCTION_SUPABASE_PROJECT_REF = "suhprttgrhyaxnqykkht";

export const RUNTIME_CAPABILITIES = Object.freeze({
  PAYMENT_OPERATIONS: "paymentOperations",
  LOCAL_PRINTING: "localPrinting",
  EXTERNAL_NOTIFICATIONS: "externalNotifications",
  FISCAL_ISSUANCE: "fiscalIssuance",
});

const CAPABILITY_ENV_KEYS = Object.freeze({
  [RUNTIME_CAPABILITIES.PAYMENT_OPERATIONS]:
    "REACT_APP_ENABLE_PAYMENT_OPERATIONS",
  [RUNTIME_CAPABILITIES.LOCAL_PRINTING]: "REACT_APP_ENABLE_LOCAL_PRINTING",
  [RUNTIME_CAPABILITIES.EXTERNAL_NOTIFICATIONS]:
    "REACT_APP_ENABLE_EXTERNAL_NOTIFICATIONS",
  [RUNTIME_CAPABILITIES.FISCAL_ISSUANCE]:
    "REACT_APP_ENABLE_FISCAL_ISSUANCE",
});

const CAPABILITY_LABELS = Object.freeze({
  [RUNTIME_CAPABILITIES.PAYMENT_OPERATIONS]:
    "los cobros y las conexiones con proveedores de pago",
  [RUNTIME_CAPABILITIES.LOCAL_PRINTING]: "la impresion local",
  [RUNTIME_CAPABILITIES.EXTERNAL_NOTIFICATIONS]:
    "las notificaciones externas",
  [RUNTIME_CAPABILITIES.FISCAL_ISSUANCE]: "la emision fiscal",
});

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const stripIpv6Brackets = (value) => {
  const normalized = normalizeText(value);

  if (normalized.startsWith("[") && normalized.endsWith("]")) {
    return normalized.slice(1, -1);
  }

  return normalized;
};

const readEnv = (key) => String(process.env[key] || "").trim();

const readOptionalBooleanEnv = (key) => {
  const normalized = normalizeText(readEnv(key));

  if (["1", "true", "yes", "on"].includes(normalized)) {
    return true;
  }

  if (["0", "false", "no", "off"].includes(normalized)) {
    return false;
  }

  return null;
};

const normalizeRuntimeEnvironment = (value) => {
  const normalized = normalizeText(value);

  if (["production", "beta", "development"].includes(normalized)) {
    return normalized;
  }

  return "";
};

export const getCurrentRuntimeHostname = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return normalizeText(window.location.hostname);
};

export const inferRuntimeEnvironmentFromHostname = (hostname) => {
  const normalized = stripIpv6Brackets(hostname);

  if (BETA_HOSTS.has(normalized)) {
    return "beta";
  }

  if (PRODUCTION_HOSTS.has(normalized)) {
    return "production";
  }

  if (LOCAL_HOSTS.has(normalized)) {
    return "development";
  }

  return "";
};

export const getSupabaseProjectRef = (supabaseUrl = "") => {
  try {
    const parsedUrl = new URL(String(supabaseUrl || "").trim());
    const [projectRef, ...hostnameParts] = parsedUrl.hostname.split(".");

    if (
      hostnameParts.join(".") !== "supabase.co" ||
      !/^[a-z0-9-]+$/i.test(projectRef || "")
    ) {
      return "";
    }

    return normalizeText(projectRef);
  } catch {
    return "";
  }
};

export const getRuntimeEnvironment = ({
  hostname = getCurrentRuntimeHostname(),
  configuredEnvironment = readEnv("REACT_APP_RUNTIME_ENV"),
} = {}) => {
  const explicitEnvironment = normalizeRuntimeEnvironment(
    configuredEnvironment
  );

  if (explicitEnvironment) {
    return explicitEnvironment;
  }

  const hostnameEnvironment = inferRuntimeEnvironmentFromHostname(hostname);

  if (hostnameEnvironment) {
    return hostnameEnvironment;
  }

  return process.env.NODE_ENV === "production" ? "production" : "development";
};

export const getRuntimeSafetyReport = ({
  hostname = getCurrentRuntimeHostname(),
  configuredEnvironment = readEnv("REACT_APP_RUNTIME_ENV"),
  supabaseUrl = readEnv("REACT_APP_SUPABASE_URL"),
} = {}) => {
  const normalizedHostname = normalizeText(hostname);
  const explicitEnvironment = normalizeRuntimeEnvironment(
    configuredEnvironment
  );
  const hostnameEnvironment =
    inferRuntimeEnvironmentFromHostname(normalizedHostname);
  const runtimeEnvironment = getRuntimeEnvironment({
    hostname: normalizedHostname,
    configuredEnvironment: explicitEnvironment,
  });
  const supabaseProjectRef = getSupabaseProjectRef(supabaseUrl);
  const reasons = [];

  if (
    explicitEnvironment &&
    hostnameEnvironment &&
    explicitEnvironment !== hostnameEnvironment
  ) {
    reasons.push(
      `El build ${explicitEnvironment} fue publicado en un dominio ${hostnameEnvironment}.`
    );
  }

  if (
    runtimeEnvironment === "beta" &&
    supabaseProjectRef === PRODUCTION_SUPABASE_PROJECT_REF
  ) {
    reasons.push("Beta esta conectado al proyecto Supabase de produccion.");
  }

  if (
    runtimeEnvironment === "production" &&
    supabaseProjectRef &&
    supabaseProjectRef !== PRODUCTION_SUPABASE_PROJECT_REF
  ) {
    reasons.push(
      "Produccion esta conectado a un proyecto Supabase diferente del autorizado."
    );
  }

  return {
    isSafe: reasons.length === 0,
    reasons,
    runtimeEnvironment,
    hostnameEnvironment,
    explicitEnvironment,
    supabaseProjectRef,
  };
};

export const isRuntimeCapabilityEnabled = (
  capability,
  {
    runtimeEnvironment = getRuntimeEnvironment(),
    configuredValue,
  } = {}
) => {
  const envKey = CAPABILITY_ENV_KEYS[capability];
  const explicitValue =
    typeof configuredValue === "boolean"
      ? configuredValue
      : envKey
        ? readOptionalBooleanEnv(envKey)
        : null;

  if (explicitValue !== null) {
    return explicitValue;
  }

  if (runtimeEnvironment === "production") {
    return capability !== RUNTIME_CAPABILITIES.FISCAL_ISSUANCE;
  }

  return false;
};

export const assertRuntimeCapability = (capability) => {
  const safetyReport = getRuntimeSafetyReport();

  if (!safetyReport.isSafe) {
    throw new Error(
      `Operacion bloqueada por seguridad. ${safetyReport.reasons.join(" ")}`
    );
  }

  if (isRuntimeCapabilityEnabled(capability)) {
    return;
  }

  const capabilityLabel = CAPABILITY_LABELS[capability] || "esta operacion";

  throw new Error(
    `El entorno ${safetyReport.runtimeEnvironment} tiene deshabilitados ${capabilityLabel}.`
  );
};

